import { update, join, keys } from 'ramda'
import { UseFormSetValue } from 'react-hook-form'
import { getPlaceData, buildShopSearchForm } from '@pcln/build-shop-search-form'
import moment from 'moment'
import { INVALID } from '@/constants/analytics'
import { HOTEL_FORM_STATE_TYPE } from '../../types'
import { MULTI_HOTEL_FORM_STATE_TYPE, HOTEL_DETAILS } from './types'

type MultiHotelErrorType = Record<string, { message: string; type: string }>

export type MutliHotelFormErrorsType = {
  hotels: MultiHotelErrorType[]
}

const HOTEL_IDS = [0, 1, 2, 3, 4]

export const getInitialState = (
  singleHotelFormDetails: Partial<HOTEL_FORM_STATE_TYPE> | null | undefined
) => {
  const {
    endDate = '',
    startDate = '',
    endLocation = null,
    roomCount = 1,
    adultCount = 2,
    childrenCount = 0,
    childrenAges = []
  } = singleHotelFormDetails || {}
  return {
    hotels: [
      {
        endLocation,
        startDate,
        endDate,
        id: 0
      },
      {
        startDate: '',
        endDate: '',
        endLocation: null,
        id: 1
      }
    ],
    roomCount,
    adultCount,
    childrenCount,
    childrenAges
  }
}

export const fields = [
  'hotels',
  'roomCount',
  'adultCount',
  'childrenCount',
  'childrenAges'
] as const

export const setHotels = (
  hotels: HOTEL_DETAILS[],
  data: Partial<HOTEL_DETAILS>,
  setValue: UseFormSetValue<MULTI_HOTEL_FORM_STATE_TYPE>,
  index = 0
): void => {
  const hotelToAdd = { ...hotels[index], ...data }
  const result = update(index, hotelToAdd, hotels)
  setValue('hotels', result, { shouldDirty: true })
}

const getFirstUnusedId = (hotels: HOTEL_DETAILS[]) => {
  const unusedIds = HOTEL_IDS.filter(id => !hotels.find(h => h.id === id))
  return unusedIds[0]
}

export const addDestination = (
  hotels: HOTEL_DETAILS[],
  setValue: UseFormSetValue<MULTI_HOTEL_FORM_STATE_TYPE>
) => {
  if (hotels.length > 4) return
  const hotelToAdd = {
    endLocation: null,
    startDate: '',
    endDate: '',
    id: getFirstUnusedId(hotels)
  }
  const result = [...hotels, hotelToAdd]
  setValue('hotels', result, { shouldDirty: true })
}

export const removeDestination = (
  hotels: HOTEL_DETAILS[],
  setValue: UseFormSetValue<MULTI_HOTEL_FORM_STATE_TYPE>,
  index: number
) => {
  const result = hotels.filter((_, hotelIndex) => hotelIndex !== index)
  setValue('hotels', result, { shouldDirty: true })
}

const buildHotels = (hotels: HOTEL_DETAILS[], roomCount: number) => {
  return hotels.map((hotel, index) => ({
    type: 'STAY',
    productId: 5,
    index,
    query: {
      travelStartDate: {
        value: moment(hotel.startDate).format('YYYY-MM-DD')
      },
      travelEndDate: {
        value: moment(hotel.endDate).format('YYYY-MM-DD')
      },
      totalNumRooms: roomCount,
      destination: getPlaceData(hotel.endLocation)
    }
  }))
}
const getEarliestDate = (hotels: HOTEL_DETAILS[]) => {
  const dates = hotels.map(h => moment(h.startDate))
  return moment.min(dates).format('MM/DD/YYYY')
}

const getLatestDate = (hotels: HOTEL_DETAILS[]) => {
  const dates = hotels.map(h => moment(h.endDate))
  return moment.max(dates).format('MM/DD/YYYY')
}
export const handleMultiHotelSubmit = (data: MULTI_HOTEL_FORM_STATE_TYPE) => {
  const { hotels, roomCount, adultCount, childrenCount, childrenAges } = data
  const childrenAgesMock: Record<string, number> = {}
  if (childrenCount > 0) {
    childrenAges.forEach((item, i) => {
      const index = i + 1
      const key = `child${index}`
      childrenAgesMock[key] = item
    })
  }
  const searchRequest = {
    rooms: roomCount,
    adultCount,
    childCount: childrenCount,
    children: childrenAgesMock,
    pivotComponentKey: 0,
    components: buildHotels(hotels, roomCount),
    arrivalLocation: hotels[0].endLocation,
    departureLocation: hotels[0].endLocation,
    departureDate: getEarliestDate(hotels),
    returnDate: getLatestDate(hotels)
  }

  const url = '/shop/search/itinerary'
  const form = buildShopSearchForm(url, searchRequest)
  form.submit()
}

const buildHotelErrors = (hotelErrors: MultiHotelErrorType[]) =>
  hotelErrors
    .map((errors, index) => {
      return `Hotel${index}: ${join(' | ', keys(errors))}`
    })
    .join(', ')

export const buildMultiHotelAnalyticsLabel = (
  errors: MutliHotelFormErrorsType
): string => {
  const { hotels = [], ...rest } = errors
  const hotelErrors = buildHotelErrors(hotels)
  return `${INVALID} - ${join(', ', [hotelErrors, ...keys(rest)])}`
}
